import axios from '@/lib/axios'
import { useQuery } from '@tanstack/vue-query'
import { ComputedRef, computed, watch } from 'vue'

import type { CompanyRequestsResponse } from '../interfaces/companyRequest'

interface Params {
	companyId?: number
	page?: number
	perPage?: number
	filters?: Filters
}

interface Filters {
	dates?: string[] | null
	creationDates?: string[] | null
	status?: string
	travelerId?: number
	createdAt?: string[] | null
	orderBy?: string
	orderDirection?: 'asc' | 'desc'
}

const getCompanyFlightRequests = async (
	params: Params
): Promise<CompanyRequestsResponse> => {
	const { data } = await axios.get(
		`/api/companies/${params.companyId}/requests`,
		{
			params: {
				service: 2,
				page: params.page || 1,
				perPage: params.perPage || 10,
				checkOutDates: params.filters?.dates,
				createdDate: params.filters?.creationDates,
				status: params.filters?.status,
				traveler: params.filters?.travelerId,
				createdAt: params.filters?.createdAt,
				sortBy: params.filters?.orderBy,
				sortDirection: params.filters?.orderDirection,
			},
		}
	)

	return data
}

export default function useCompanyFlightRequests(params: ComputedRef<Params>) {
	const enabled = computed(() => !!params.value.companyId)

	const queryKey = computed(() => [
		'companyFlightRequests',
		params.value.companyId,
		params.value.page,
		params.value.perPage,
		params.value.filters?.dates,
		params.value.filters?.creationDates,
		params.value.filters?.status,
		params.value.filters?.travelerId,
		params.value.filters?.createdAt,
		params.value.filters?.orderBy,
		params.value.filters?.orderDirection,
	])

	const { data, isLoading, isFetching, error, refetch, status } = useQuery(
		queryKey as any,
		() => getCompanyFlightRequests(params.value),
		{
			enabled,
			staleTime: 5 * 60 * 1000,
			cacheTime: 10 * 60 * 1000,
			refetchOnWindowFocus: false,
		}
	)

	watch(
		params,
		() => {
			if (params.value.companyId) {
				refetch()
			}
		}
	)

	const requests = computed(() => data.value?.data || [])
	const meta = computed(() => data.value?.meta)
	const links = computed(() => data.value?.links)

	return {
		requests,
		meta,
		links,
		isLoading: computed(() => enabled.value && isLoading.value),
		isFetching,
		error,
		refetch,
		status,
	}
}
